import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UnauthorizedComponent } from 'src/app/modules/shared/unauthorized/unauthorized.component';
import { TranslocoService } from '@ngneat/transloco';
import { ApiService } from '../../api/api.service';
import { MatDialog } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { ErrorHandlerService } from '../../error-handler.service';
import { isNewErrorSchema } from 'src/app/utils/is-new-error-schema';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ConfigService } from '../../config/config.service';
import { ThemeNames } from 'src/app/models/themes.enum';
import { UnauthorizedV2Component } from 'src/app/components/blue-pulse-theme-myprofile/components/unauthorized-v2/unauthorized-v2.component';

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  private session = [];
  private localSession = [];
  constructor(
    private dialog: MatDialog,
    private transloco: TranslocoService,
    private api: ApiService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly config: ConfigService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isBluePulseTheme = this.config._envConfig?.ThemeName === ThemeNames.BLUE_PULSE;

    this.session = Object.keys(sessionStorage);
    let user = null;
    let tokenJson = null;

    if (this.session.length) {
      user = this.session.find(key => key.indexOf('oidc.user:') > -1);

      if (user) tokenJson = JSON.parse(sessionStorage.getItem(user));
    }

    if (user == null) {
      // refer to local storage when session was not found
      // used to transfer session
      this.localSession = Object.keys(localStorage);

      if (this.localSession.length) {
        user = this.localSession.find(key => key.indexOf('session-transfer') > -1);

        if (user) {
          const userSessionToken = JSON.parse(localStorage.getItem(user));
          tokenJson = {
            access_token: userSessionToken.accessToken,
            token_type: 'Bearer',
          };
        }
      }
    }
    
    const headers: any = { 'Accept-Language': this.getActiveLanguage(), };
    if (tokenJson) {
      headers['Authorization'] = `${tokenJson.token_type} ${tokenJson.access_token}`;
    }
    request = request.clone({ setHeaders: headers, });

    return next.handle(request).pipe(
      catchError(error => {
        const errorResponse = error.error || error.Error;
        const errorStatus = error.status || error.Status;

        if (isNewErrorSchema(errorResponse)) {
          this.errorHandlerService.handleError(error);
          this.toastrService.toastrConfig.preventDuplicates = true;

          const originalFn = this.toastrService.error;
          this.toastrService.error = (() => {}) as any;
          setTimeout(() => {
            this.toastrService.error = originalFn;
          }, 300);
        } else if (errorStatus === 401 || errorStatus === 403) {
          
          // hide UnauthorizedComponent popup when viewing the registration page
          const path = this.router.url;
          if (path.includes("/register/")) return;

          const popup: ComponentType<any> = isBluePulseTheme ? UnauthorizedV2Component : UnauthorizedComponent;
          const opts = {
            disableClose: true,
            width: isBluePulseTheme ? '450px' : null, 
            maxWidth: isBluePulseTheme ? '100vw' : null, 
            panelClass: isBluePulseTheme ? 'blue-pulse-dialog' : '',
          }
          setTimeout(() => {
            this.dialog.closeAll();
            this.dialog.open(popup, opts);
          }, 1050);
        }
        return throwError(error);
      }),
    );
  }

  private getActiveLanguage(): string {
    let activeLang = this.transloco.getActiveLang().toLowerCase();
    return this.api.formatAcceptLanguage(activeLang);
  }
}
